import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Button,
  Center,
  Divider,
  Loader,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconMail } from '@tabler/icons-react';
import { useAuth } from './AuthProvider';
import {
  clearPendingEmail,
  completeMagicLinkSignIn,
  isMagicLinkUrl,
  readPendingEmail,
  sendMagicLink,
} from './emailLink';

interface LocationState {
  from?: { pathname: string };
}

type Mode = 'password' | 'magic-link';

/**
 * Phase machine for the page:
 *  - 'idle'        : showing the chosen mode's form (password or magic-link)
 *  - 'sending'     : magic-link send in flight
 *  - 'sent'        : magic-link sent, waiting for user to click email
 *  - 'completing'  : returned from email link, calling signInWithEmailLink
 *  - 'need-email'  : returned from email link on a device without stored email
 */
type Phase = 'idle' | 'sending' | 'sent' | 'completing' | 'need-email';

export function LoginPage() {
  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('magic-link');
  const [phase, setPhase] = useState<Phase>('idle');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const redirectTarget = (location.state as LocationState | null)?.from?.pathname ?? '/';

  // On mount: if we landed here from an email-link click, finish the sign-in.
  useEffect(() => {
    if (!isMagicLinkUrl(window.location.href)) return;
    const stored = readPendingEmail();
    if (stored) {
      setPhase('completing');
      completeMagicLinkSignIn(stored, window.location.href).catch((err) => {
        setPhase('need-email');
        setError(formatAuthError((err as { code?: string }).code));
      });
    } else {
      setPhase('need-email');
    }
  }, []);

  if (loading) return null;
  // Treat an anonymous (guest) session as "not signed in" here, so a guest can
  // still upgrade to a real account from this page instead of being bounced.
  if (user && !user.isAnonymous) {
    return <Navigate to={redirectTarget} replace />;
  }

  const onPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPhase('sending');
    setError(null);
    try {
      await signIn(email.trim(), password);
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      setError(formatAuthError((err as { code?: string }).code));
      setPhase('idle');
    }
  };

  const onMagicLinkSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPhase('sending');
    setError(null);
    try {
      await sendMagicLink(email);
      setPhase('sent');
    } catch (err) {
      setError(formatAuthError((err as { code?: string }).code));
      setPhase('idle');
    }
  };

  const onCompleteWithEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPhase('completing');
    setError(null);
    try {
      await completeMagicLinkSignIn(email, window.location.href);
      // user state will flip via onAuthStateChanged → user redirect above
    } catch (err) {
      setError(formatAuthError((err as { code?: string }).code));
      setPhase('need-email');
    }
  };

  return (
    <Center mih="100vh" px="md">
      <Paper withBorder shadow="md" p="xl" radius="md" maw={420} w="100%">
        <Stack gap="lg">
          <Stack gap="xs" align="center">
            <img src="/logo.png" alt="Morrell Dev" width={64} height={64} />
            <Title order={2}>Morrell Dev</Title>
            <Text size="sm" c="dimmed" ta="center">
              {phase === 'sent'
                ? 'Check your inbox.'
                : phase === 'completing'
                  ? 'Signing you in…'
                  : phase === 'need-email'
                    ? 'Confirm your email to finish.'
                    : 'Sign in to continue.'}
            </Text>
          </Stack>

          {phase === 'completing' && (
            <Center py="md">
              <Loader />
            </Center>
          )}

          {phase === 'sent' && (
            <Alert color="teal" icon={<IconCheck size={16} />} variant="light">
              We sent a sign-in link to <b>{email}</b>. Open it on this device to finish.
            </Alert>
          )}

          {phase === 'need-email' && (
            <form onSubmit={onCompleteWithEmailSubmit}>
              <Stack gap="md">
                <Text size="sm">
                  Enter the email address you used to request the link. Single-use codes are tied
                  to the device that sent the link, so we can't auto-fill on a different device.
                </Text>
                <TextInput
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  required
                  autoFocus
                />
                {error && (
                  <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
                    {error}
                  </Alert>
                )}
                <Button type="submit" fullWidth>
                  Sign in
                </Button>
                <Anchor
                  size="xs"
                  c="dimmed"
                  onClick={(e) => {
                    e.preventDefault();
                    clearPendingEmail();
                    window.history.replaceState(null, '', '/login');
                    setPhase('idle');
                    setError(null);
                  }}
                  href="#"
                >
                  Cancel and start over
                </Anchor>
              </Stack>
            </form>
          )}

          {phase === 'idle' && mode === 'password' && (
            <form onSubmit={onPasswordSubmit}>
              <Stack gap="md">
                <TextInput
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  required
                  autoComplete="email"
                  autoFocus
                />
                <PasswordInput
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  required
                  autoComplete="current-password"
                />
                {error && (
                  <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
                    {error}
                  </Alert>
                )}
                <Button type="submit" fullWidth>
                  Sign in
                </Button>
              </Stack>
            </form>
          )}

          {phase === 'idle' && mode === 'magic-link' && (
            <form onSubmit={onMagicLinkSubmit}>
              <Stack gap="md">
                <TextInput
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  required
                  autoComplete="email"
                  autoFocus
                />
                {error && (
                  <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
                    {error}
                  </Alert>
                )}
                <Button type="submit" fullWidth leftSection={<IconMail size={16} />}>
                  Email me a sign-in link
                </Button>
              </Stack>
            </form>
          )}

          {phase === 'sending' && (
            <Center py="md">
              <Loader />
            </Center>
          )}

          {(phase === 'idle' || phase === 'sent') && (
            <>
              <Divider label="or" labelPosition="center" />
              <Anchor
                size="sm"
                ta="center"
                onClick={(e) => {
                  e.preventDefault();
                  setError(null);
                  setPhase('idle');
                  setMode(mode === 'password' ? 'magic-link' : 'password');
                }}
                href="#"
              >
                {mode === 'password' ? 'Sign in with email link instead' : 'Sign in with password instead'}
              </Anchor>
            </>
          )}
        </Stack>
      </Paper>
    </Center>
  );
}

function formatAuthError(code: string | undefined): string {
  switch (code) {
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/user-disabled':
      return 'This account is disabled.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again in a moment.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection.';
    case 'auth/missing-email':
      return 'Email is required.';
    case 'auth/expired-action-code':
      return 'This sign-in link has expired. Request a new one.';
    case 'auth/invalid-action-code':
      return 'This sign-in link is invalid or already used.';
    case 'auth/operation-not-allowed':
      return 'Email link sign-in is not enabled. Check Firebase Console.';
    case 'auth/unauthorized-continue-uri':
      return 'This domain is not authorized for sign-in links. Add it in Firebase Console.';
    default:
      return 'Sign-in failed. Try again.';
  }
}
